import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import Icon from '../Icon';
import Button from '../ui/Button';
import ConfirmDialog from '../ui/ConfirmDialog';
import { MaskedValue } from '../ui/MaskedValue';
import { useNotifications } from '../NotificationProvider';
import { reportSaveError, useFieldErrors } from '../forms/_shared';
import { loadClients, Client } from '../../services/ClientService';
import {
  getClientInvoiceById,
  createClientInvoice,
  updateClientInvoice,
  deleteClientInvoicePayment,
  ClientInvoice,
} from '../../services/ClientInvoiceService';
import type { LineItem } from '../ui/LineItemsEditor';
import { formatMoney } from '../../utils/currency';
import { blankLineItem, toEditorItems, fromEditorItems, computeTotals } from '../../utils/lineItemDraft';
import { invoicePaymentPath } from '../../utils/accountRoutes';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveProjectId } from '../../utils/session';
import { readBillingPrefill } from '../../utils/projectBilling';
import AccountFormPage from './AccountFormPage';
import DocumentFormBody, { DocumentFormValues } from './DocumentFormBody';

interface InvoiceFormPageProps {
  invoiceId?: string | null;
}

const LIST_PATH = '/invoices';
const EMPTY: DocumentFormValues = { projectId: '', clientId: '', taxRate: '0', date: '', notes: '' };
const PAYABLE = ['sent', 'partial', 'overdue'];

export const InvoiceFormPage: React.FC<InvoiceFormPageProps> = ({ invoiceId = null }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  const fieldErrors = useFieldErrors();
  const { workspaces } = useAuth();
  const { search } = useLocation();

  const [loading, setLoading] = useState(!!invoiceId);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  // A new invoice opened from a project's Billing panel starts with that project
  // and its client chosen; otherwise it starts on the project you are in.
  const [values, setValues] = useState<DocumentFormValues>(() => {
    if (invoiceId) return EMPTY;
    const prefill = readBillingPrefill(search);
    return { ...EMPTY, projectId: prefill.projectId || getActiveProjectId() || '', clientId: prefill.clientId };
  });
  const [lineItems, setLineItems] = useState<LineItem[]>(() => [blankLineItem()]);
  const [invoice, setInvoice] = useState<ClientInvoice | null>(null);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    loadClients('', 'active').then(setClients).catch(() => { /* the picker just stays empty */ });
  }, []);

  const applyInvoice = useCallback((inv: ClientInvoice) => {
    setInvoice(inv);
    setValues({ projectId: inv.project_id ?? inv.wedding_id ?? '', clientId: inv.client_id, taxRate: String(inv.tax_rate ?? 0), date: inv.due_date ?? '', notes: inv.notes ?? '' });
    setLineItems(toEditorItems(inv.items));
  }, []);

  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;
    setLoading(true);
    getClientInvoiceById(invoiceId)
      .then(inv => { if (!cancelled) applyInvoice(inv); })
      .catch(err => {
        if (cancelled) return;
        showError(err instanceof Error ? err.message : 'Could not open this invoice.');
        navigate(LIST_PATH, { replace: true });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [invoiceId, applyInvoice, navigate, showError]);

  const parsedItems = useMemo(() => fromEditorItems(lineItems), [lineItems]);
  const taxRateNum = Number(values.taxRate) || 0;
  const totals = useMemo(() => computeTotals(parsedItems, taxRateNum), [parsedItems, taxRateNum]);

  const clientOptions = useMemo(() => {
    const opts = clients.map(c => ({ value: c.id, label: c.name }));
    if (values.clientId && !opts.some(o => o.value === values.clientId)) {
      opts.push({ value: values.clientId, label: invoice?.client_name || 'Client' });
    }
    return opts;
  }, [clients, values.clientId, invoice]);

  const projectOptions = useMemo(() => {
    const opts = workspaces.map(w => ({ value: w.project_id, label: w.project_name }));
    if (values.projectId && !opts.some(o => o.value === values.projectId)) {
      opts.push({ value: values.projectId, label: 'Project' });
    }
    return opts;
  }, [workspaces, values.projectId]);

  const readOnly = !!invoiceId && (invoice?.status !== 'draft' || !!invoice?.archived);
  // Every invoice belongs to a project, so a new one cannot be saved without one.
  const canSave = !!values.clientId && (!!invoiceId || !!values.projectId) && parsedItems.length > 0;

  const handleSave = async () => {
    if (!canSave || saving) return;
    fieldErrors.clear();
    setSaving(true);
    try {
      const body = {
        client_id: values.clientId,
        ...(invoiceId || !values.projectId ? {} : { project_id: values.projectId }),
        items: parsedItems,
        tax_rate: taxRateNum,
        due_date: values.date || undefined,
        notes: values.notes.trim() || undefined,
      };
      if (invoiceId) {
        await updateClientInvoice(invoiceId, body);
        showSuccess('Invoice updated.');
      } else {
        await createClientInvoice(body);
        showSuccess('Invoice created.');
      }
      navigate(LIST_PATH);
    } catch (err) {
      reportSaveError(err, fieldErrors, showError, 'Failed to save invoice.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePayment = async () => {
    if (!invoiceId || !removeTarget || removing) return;
    setRemoving(true);
    try {
      applyInvoice(await deleteClientInvoicePayment(invoiceId, removeTarget));
      showSuccess('Payment removed.');
      setRemoveTarget(null);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove payment.');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const payments = invoice?.payments ?? [];
  const canRecordPayment = !!invoice && PAYABLE.includes(invoice.status) && !invoice.archived;
  const number = invoice?.invoice_number ?? '';

  return (
    <AccountFormPage
      view="invoice-form"
      leaf={invoiceId ? number || 'Invoice' : 'New invoice'}
      title={invoiceId ? `Invoice ${number}` : 'New invoice'}
      onSave={handleSave}
      onCancel={() => navigate(LIST_PATH)}
      saveLabel={invoiceId ? 'Save' : 'Create invoice'}
      canSave={canSave}
      saving={saving}
      readOnly={readOnly}
      extraActions={canRecordPayment && invoiceId ? (
        <Button variant="outline" size="sm" icon={<Icon name="dollar" size={14} />} onClick={() => navigate(invoicePaymentPath(invoiceId))}>
          <span className="as-btn-label">Record payment</span>
        </Button>
      ) : undefined}
    >
      {readOnly && (
        <p className="ap-note">
          {invoice?.archived ? 'This invoice is archived.' : `This invoice is ${invoice?.status}, so its lines can no longer be edited.`}
        </p>
      )}
      <DocumentFormBody
        values={values}
        onChange={setValues}
        lineItems={lineItems}
        onLineItemsChange={setLineItems}
        clientOptions={clientOptions}
        projectOptions={projectOptions}
        projectRequired
        projectLocked={!!invoiceId}
        dateLabel="Due Date"
        currency={invoice?.currency}
        totals={totals}
        taxRateNum={taxRateNum}
        clientLocked={!!invoiceId}
        readOnly={readOnly}
        fieldErrors={fieldErrors}
      />

      {invoice && (
        <section aria-labelledby="invoice-payments-title">
          <h2 className="ap-section-title" id="invoice-payments-title">Payments</h2>
          <p className="ap-note">
            Paid {formatMoney(invoice.amount_paid, invoice.currency)} of {formatMoney(invoice.total, invoice.currency)}
            {' · '}Balance due {formatMoney(invoice.balance_due, invoice.currency)}
          </p>
          {payments.length === 0 ? (
            <p className="ap-note">No payments recorded yet.</p>
          ) : (
            <div className="ap-payment-list">
              {payments.map(p => (
                <div key={p.id} className="ap-payment-row">
                  <div>
                    <strong>{formatMoney(p.amount, invoice.currency)}</strong>
                    <span className="ap-payment-meta">
                      {p.paid_at}{p.method ? ` · ${p.method}` : ''}
                      {p.reference ? <> · <MaskedValue value={p.reference} /></> : null}
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" aria-label="Remove payment" title="Remove payment" onClick={() => setRemoveTarget(p.id)}>
                    <Icon name="trash" size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemovePayment}
        title="Remove this payment?"
        description="The invoice balance goes back up by the amount of this payment."
        confirmLabel="Remove payment"
        loading={removing}
      />
    </AccountFormPage>
  );
};

export default InvoiceFormPage;
